import { c as _c } from "react/compiler-runtime";
import { useI18n } from '@/context/I18nProvider';
import { cn } from '@/lib/utils';
import { ConversationEvent } from '@/services/types/Lead.types';
import { fDateTime } from '@/utils/format-time';
import { CopyX, ShieldAlert, Sparkles, type LucideIcon } from 'lucide-react';
type ConversationEventRowProps = {
  event: ConversationEvent;
  /** The conversation moved on after the decision: informational only. */
  obsolete: boolean;
  /** Offer the "nudge with AI" button on this event. */
  canNudge: boolean;
  /** Why the nudge button is unavailable, when it is. */
  nudgeDisabledReason?: 'aiDisabled' | 'windowClosed' | null;
  isNudging: boolean;
  hasNudged: boolean;
  onNudge: () => void;
};
type Tone = {
  Icon: LucideIcon;
  box: string;
  icon: string;
  title: string;
};
const TONES: Record<string, Tone> = {
  followups_not_generated: {
    Icon: Sparkles,
    box: 'border-purple-200 bg-purple-50',
    icon: 'text-purple-600',
    title: 'text-purple-800'
  },
  followup_blocked: {
    Icon: ShieldAlert,
    box: 'border-amber-200 bg-amber-50',
    icon: 'text-amber-600',
    title: 'text-amber-800'
  },
  followup_dropped_duplicate: {
    Icon: CopyX,
    box: 'border-gray-200 bg-gray-50',
    icon: 'text-gray-500',
    title: 'text-gray-700'
  }
};
const TITLE_KEYS = {
  followups_not_generated: 'conversations.events.followupsNotGenerated',
  followup_blocked: 'conversations.events.followupBlocked',
  followup_dropped_duplicate: 'conversations.events.followupDroppedDuplicate'
} as const;

/**
 * A system note in the timeline: a follow-up that never went out, pinned
 * exactly where it would have appeared, with the reason and both dates.
 * The point is to make a lead who silently stopped being nudged visible —
 * and to let the user nudge them by hand from right there.
 */
const ConversationEventRow = t0 => {
  const $ = _c(64);
  const {
    event,
    obsolete,
    canNudge,
    nudgeDisabledReason,
    isNudging,
    hasNudged,
    onNudge
  } = t0;
  const {
    t
  } = useI18n();
  const tone = TONES[event.Type] ?? TONES.followup_dropped_duplicate;
  const titleKey = TITLE_KEYS[event.Type as keyof typeof TITLE_KEYS] ?? TITLE_KEYS.followup_dropped_duplicate;
  const {
    Icon
  } = tone;
  const t1 = obsolete && "opacity-60";
  let t2;
  if ($[0] !== t1 || $[1] !== tone.box) {
    t2 = cn("flex w-full max-w-xl flex-col gap-1.5 rounded-lg border px-3 py-2 text-xs", tone.box, t1);
    $[0] = t1;
    $[1] = tone.box;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== tone.icon) {
    t3 = cn("mt-0.5 size-3.5 shrink-0", tone.icon);
    $[3] = tone.icon;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== Icon || $[6] !== t3) {
    t4 = <Icon className={t3} />;
    $[5] = Icon;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== tone.title) {
    t5 = cn("font-medium", tone.title);
    $[8] = tone.title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t || $[11] !== titleKey) {
    t6 = t(titleKey);
    $[10] = t;
    $[11] = titleKey;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== event.Detail || $[17] !== event.Type || $[18] !== t) {
    t8 = event.Type === "followups_not_generated" && event.Detail ? <span className="text-text-primary">{t("conversations.events.aiReason", {
        reason: event.Detail
      })}</span> : null;
    $[16] = event.Detail;
    $[17] = event.Type;
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== event.RelatedContent || $[21] !== t) {
    t9 = event.RelatedContent ? <span className="text-text-primary break-words">{t("conversations.events.droppedContent", {
        content: event.RelatedContent
      })}</span> : null;
    $[20] = event.RelatedContent;
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== event.Detail || $[24] !== event.Type) {
    t10 = event.Type === "followup_blocked" && event.Detail ? <span className="text-muted-foreground">{event.Detail}</span> : null;
    $[23] = event.Detail;
    $[24] = event.Type;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== event.Timestamp || $[27] !== t) {
    t11 = t("conversations.events.plannedAt", {
      date: fDateTime(event.Timestamp, "dd MMM yyyy HH:mm:ss")
    });
    $[26] = event.Timestamp;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== event.CreatedAt || $[30] !== t) {
    t12 = t("conversations.events.decidedAt", {
      date: fDateTime(event.CreatedAt, "dd MMM yyyy HH:mm:ss")
    });
    $[29] = event.CreatedAt;
    $[30] = t;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <span className="text-muted-foreground tabular-nums">{t11}{" \xB7 "}{t12}</span>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== obsolete || $[36] !== t) {
    t14 = obsolete ? <span className="italic text-muted-foreground">{t("conversations.events.obsolete")}</span> : null;
    $[35] = obsolete;
    $[36] = t;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t10 || $[39] !== t13 || $[40] !== t14 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t15 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t7}{t8}{t9}{t10}{t13}{t14}</div>;
    $[38] = t10;
    $[39] = t13;
    $[40] = t14;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== canNudge || $[46] !== hasNudged || $[47] !== isNudging || $[48] !== onNudge || $[49] !== t) {
    t16 = canNudge ? <button type="button" onClick={onNudge} disabled={isNudging || hasNudged} className="shrink-0 self-center rounded-lg bg-purple-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-50">{hasNudged ? t("conversations.aiResponseRequested") : isNudging ? "..." : t("conversations.events.triggerFollowup")}</button> : null;
    $[45] = canNudge;
    $[46] = hasNudged;
    $[47] = isNudging;
    $[48] = onNudge;
    $[49] = t;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t15 || $[52] !== t16 || $[53] !== t4) {
    t17 = <div className="flex items-start gap-2">{t4}{t15}{t16}</div>;
    $[51] = t15;
    $[52] = t16;
    $[53] = t4;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== canNudge || $[56] !== nudgeDisabledReason || $[57] !== obsolete || $[58] !== t) {
    t18 = !obsolete && !canNudge && nudgeDisabledReason ? <span className="pl-[22px] text-muted-foreground">{nudgeDisabledReason === "aiDisabled" ? t("conversations.events.aiDisabledHint") : t("conversations.events.windowExpiredHint")}</span> : null;
    $[55] = canNudge;
    $[56] = nudgeDisabledReason;
    $[57] = obsolete;
    $[58] = t;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t2) {
    t19 = <div className="flex justify-center px-2" data-testid="conversation-event"><div className={t2}>{t17}{t18}</div></div>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t2;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  return t19;
};
export { ConversationEventRow };